import {
  Box,
  Card,
  CardContent,
  Divider,
  Drawer,
  Fab,
  List,
  ListItemButton,
  Stack,
  Typography,
} from '@mui/material'
import { useCallback, useMemo, useState } from 'react'
import {
  Button,
  Icons,
  InformationModal,
  ItemImageArea,
  MenuListItem,
} from 'utoniq-ui'

import {
  Artist,
  ConsoleMissionBoxFragment,
  Maybe,
  MembershipSubscriptionProduct,
  MissionType,
  OrderBy,
} from '@/clients/utoniq-core/schema'
import {
  ArtistHeaderItemsLeft,
  ArtistHeaderItemsLeftProps,
  ArtistHeaderItemsRight,
  Head,
} from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import {
  ConsoleMissionBox,
  ConsoleMissionBoxSkeleton,
} from '@/components/organisms'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { ArtistThemeProvider, styles, styleSets } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, Template } from '@/core/architecture'

import { SearchForm, SearchFormValues } from './SearchForm'

export * from './fragments'

export type State = {
  user: ArtistHeaderItemsLeftProps['currentUser']
  artistId: string
  draftItem: Maybe<ConsoleMissionBoxFragment>
  missions: Maybe<{
    edges: { node: ConsoleMissionBoxFragment }[]
  }>
  artist: Maybe<
    Pick<Artist, 'id' | 'paymentType' | 'hasArrangingDeliveryItems'>
  >
  deletingMission: boolean
  loading: boolean
  hasNextPage: boolean
  sort: OrderBy
  membership: Pick<MembershipSubscriptionProduct, 'productId'>[] | undefined
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onSearch: (value: SearchFormValues) => Promise<void>
  onDelete: (data: ConsoleMissionBoxFragment) => Promise<void>
  onFetchMore: () => Promise<void>
  onDraftConfirm: (data: ConsoleMissionBoxFragment) => Promise<void>
}

export const ConsoleMissionsTemplate: Template<State, Action> = ({
  state,
  onSearch,
  onDelete,
  onFetchMore,
  onDraftConfirm,
}) => {
  const { t } = useTranslation('consoleMissions')
  const { routerNames } = useRouter()

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ConsoleMissions} />

      <ConsoleBaseLayout
        header={{
          left: (
            <ArtistHeaderItemsLeft
              currentUser={state.user}
              paymentType={state.artist?.paymentType}
              hasArrangingDeliveryItems={
                state.artist?.hasArrangingDeliveryItems
              }
              membership={state.membership}
            />
          ),
          right: <ArtistHeaderItemsRight artistId={state.artistId} />,
        }}
      >
        <Typography sx={{ py: 3, px: 5 }} variant="h3">
          {t('title')}
        </Typography>
        <SearchForm onSubmit={onSearch} sort={state.sort} />
        <Stack sx={{ px: 5 }}>
          <ScrollFetcher
            componentProps={{ sx: { pt: 2, pb: 5 }, spacing: 5 }}
            nodes={state.missions?.edges.map(({ node }) => (
              <ConsoleMissionBox
                mission={node}
                key={node.id}
                onDelete={onDelete}
                deletingMission={state.deletingMission}
              />
            ))}
            onFetchMore={onFetchMore}
            loading={state.loading}
            hasNextPage={state.hasNextPage}
            emptyMessage={t('emptyMission')}
            loadingComponent={
              <Stack spacing={5} sx={{ pb: 7 }}>
                {Array.from(Array(4).keys()).map(key => (
                  <ConsoleMissionBoxSkeleton key={key} />
                ))}
              </Stack>
            }
          />
        </Stack>
        <BaseCell draftItem={state.draftItem} onDraftConfirm={onDraftConfirm} />
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

type BaseCellProps = {
  draftItem: State['draftItem']
  onDraftConfirm: Action['onDraftConfirm']
}

const BaseCell: Cell<BaseCellProps> = ({ draftItem, onDraftConfirm }) => {
  const { t } = useTranslation('consoleMissions')
  const { generatePath, routerPages } = useRouter()
  const [open, setOpen] = useState<boolean>(false)
  const [openModalDraft, setOpenModalDraft] = useState<boolean>(false)
  const items = useMemo(
    () => [
      {
        Icon: <Icons.Help />,
        text: t('quiz'),
        href: generatePath(routerPages.ConsoleMissionCreate, {
          missionType: MissionType.Quiz,
        }),
      },
      /**
       * @description
       * Open when function is implemented in the future
       * https://app.clickup.com/t/908127/UT-6987
       */
      // {
      //   Icon: <Icons.Rotate />,
      //   text: t('retweet'),
      //   href: generatePath(routerPages.ConsoleMissionCreate, {
      //     missionType: MissionType.TwitterRetweet,
      //   }),
      // },
      // {
      //   Icon: <Icons.MyPage />,
      //   text: t('follow'),
      //   href: generatePath(routerPages.ConsoleMissionCreate, {
      //     missionType: MissionType.TwitterFollow,
      //   }),
      // },
      // {
      //   Icon: <Icons.OwnedItems />,
      //   text: t('multipleItems'),
      //   href: generatePath(routerPages.ConsoleMissionCreate, {
      //     missionType: MissionType.ItemCollect,
      //   }),
      // },
    ],
    [generatePath, routerPages.ConsoleMissionCreate, t]
  )
  const toggleDrawer = useCallback(
    (open: boolean) => () => {
      setOpen(open)
    },
    []
  )

  return (
    <Stack direction="row-reverse">
      <Stack
        sx={{
          position: 'fixed',
          bottom: 16,
          zIndex: 5,
          maxWidth: 600,
          margin: '0 auto',
          px: 4,
          pb: styles.safeAreaBottom,
        }}
      >
        <Fab
          size="medium"
          color="primary"
          onClick={() => {
            if (draftItem) {
              return setOpenModalDraft(true)
            }
            toggleDrawer(true)()
          }}
        >
          <Icons.ThinPlus fontSize="medium" sx={{ color: 'white' }} />
        </Fab>
      </Stack>
      <Drawer open={open} onClose={toggleDrawer(false)}>
        <Box sx={{ p: 5 }}>
          <Typography variant="h6" color="grey.600" align="center">
            {t('selectMissionType')}
          </Typography>
        </Box>
        <Divider />
        <List>
          {items.map(({ Icon, text, href }, index) => (
            <MenuListItem
              key={index}
              href={href}
              title={text}
              primaryTypographyProps={{ variant: 'h6' }}
              icon={Icon}
              sx={{ px: 5 }}
            />
          ))}
          <Divider />
          <ListItemButton
            component={Box}
            sx={{ height: 52 }}
            alignItems="center"
            onClick={toggleDrawer(false)}
          >
            <Typography
              variant="h6"
              align="center"
              sx={{ width: '100%', color: 'primary.main' }}
            >
              {t('cancel')}
            </Typography>
          </ListItemButton>
        </List>
      </Drawer>
      <InformationModal
        open={openModalDraft}
        onClose={() => setOpenModalDraft(false)}
        title={t('titleDraft')}
        content={<ContentModalDraftCell data={draftItem} />}
        buttons={
          <>
            <Button
              sx={{ minWidth: 212 }}
              variant="contained"
              color="primary"
              size="large"
              loading={false}
              onClick={async () => {
                draftItem && (await onDraftConfirm(draftItem))
                setOpenModalDraft(false)
              }}
            >
              {t('okDraft')}
            </Button>
            <Button
              sx={{ minWidth: 212 }}
              variant="outlined"
              color="primary"
              size="large"
              disabled={false}
              onClick={() => {
                setOpenModalDraft(false)
              }}
            >
              {t('cancelDraft')}
            </Button>
          </>
        }
      />
    </Stack>
  )
}

type ContentModalDraftCellProps = {
  data: State['draftItem']
}
const ContentModalDraftCell: Cell<ContentModalDraftCellProps> = ({ data }) => (
  <Card elevation={0} sx={{ bgcolor: 'unset' }}>
    <CardContent sx={{ pt: 6, px: 2, pb: 2 }}>
      <Stack direction="row" spacing={3}>
        <ItemImageArea
          src={data?.reward.item?.item?.thumbnail.front?.smallUri ?? ''}
          size="x-small"
        />
        <Stack flex={1} spacing={1} justifyContent="center">
          <Typography
            sx={{ ...styleSets.lineCamp(2) }}
            variant="subtitle1"
            align="left"
          >
            {data?.title ?? ''}
          </Typography>
          <Typography color="text.secondary" variant="caption" align="left">
            {data?.formattedCreatedAt ?? ''}
          </Typography>
        </Stack>
      </Stack>
    </CardContent>
  </Card>
)
